import { Emitter } from "@core/events/emitter";
import type { AppDevSettingKey } from "@models/data/appSettingsDto";
import type { IAppSettingsModel } from "@models/settings/IAppSettingsModel";
import type { IDebugModeModel } from "./IDebugModeModel";
import type { IDevSettingsModel } from "./IDevSettingsModel";
import { runOncePerOrigin } from "@models/utils/runOncePerOrigin";

/**
 * `DevSettingsModel` — the persisted implementation of {@link IDevSettingsModel}.
 *
 * Headless, and now a thin projection: the VALUES live in the deployment's shared
 * `app-settings.json` behind {@link IAppSettingsModel}, and this model's job is
 * the part that document has no opinion about — folding the debug-mode gate into
 * developer-only overrides, preserving explicitly global reads, and enforcing
 * the setter guard. See the interface for each setting's scope.
 *
 * **No `localStorage`.** These used to be per-browser preferences under
 * `Campfire.Dev.Settings.*`; they are now deployment-wide, so a switch one person
 * flips is what everyone on the deployment gets. Nothing migrates the old keys —
 * they were developer-local by definition, and a migration would have each
 * developer's browser racing to publish its own copy as the shared truth the
 * first time they opened the panel.
 */
export class DevSettingsModel implements IDevSettingsModel {
  readonly onChanged = new Emitter<void>();

  constructor(
    private readonly m_debug: IDebugModeModel,
    private readonly m_settings: IAppSettingsModel,
  ) {
    // A gate flip changes every effective value with no preference written, so
    // readers have to be told — they only ever see the effective values. The
    // settings document landing (first load, an edit made elsewhere, or a failed
    // write rolling back) changes them for the same reason.
    // Never unsubscribed: all three are app-lifetime singletons registered in
    // the core IOC pass, and this model has no teardown path.
    this.m_debug.onChanged.on(() => this.onChanged.emit());
    this.m_settings.onChanged.on(() => this.onChanged.emit());

    runOncePerOrigin("dev-settings-to-app-settings", forgetLegacyLocalPreferences);
  }

  get userFacingWorkflowAuthoringPreference(): boolean {
    return this.m_settings.getDevSetting("userFacingWorkflowAuthoring");
  }

  get isUserFacingWorkflowAuthoringEnabled(): boolean {
    return this.m_debug.isEnabled ? this.userFacingWorkflowAuthoringPreference : true;
  }

  setUserFacingWorkflowAuthoringEnabled(enabled: boolean): void {
    this.write("userFacingWorkflowAuthoring", enabled);
  }

  get responsesVoicePreference(): boolean {
    return this.m_settings.getDevSetting("respondWithVoice");
  }

  get isResponsesVoiceEnabled(): boolean {
    // OPT-OUT: bridged Responses replies are spoken in the shipped path. The
    // override only takes effect while its debug affordance is reachable.
    return this.m_debug.isEnabled ? this.responsesVoicePreference : true;
  }

  setResponsesVoiceEnabled(enabled: boolean): void {
    this.write("respondWithVoice", enabled);
  }

  get hideSparkLabelPreference(): boolean {
    return this.m_settings.getDevSetting("hideSparkLabel");
  }

  get isSparkLabelHidden(): boolean {
    // Deployment policy, not a developer-only runtime override: the debug
    // panel guards who may WRITE it, but everyone on this deployment reads the
    // same value after the panel closes.
    return this.hideSparkLabelPreference;
  }

  setSparkLabelHidden(hidden: boolean): void {
    this.write("hideSparkLabel", hidden);
  }

  get collapsibleSparkRowPreference(): boolean {
    return this.m_settings.getDevSetting("collapsibleSparkRow");
  }

  get isCollapsibleSparkRowEnabled(): boolean {
    // OPT-OUT like responsesVoice: the collapsible row IS the desktop editing
    // layout (user direction, 2026-09-02), so an ungated build — and an account
    // where nobody has touched the switch — gets `true`, and the switch only
    // ever puts a developer back on the split bottom-centre arrangement.
    //
    // ⚠ The ungated branch must equal `K_DEV_SETTING_DEFAULTS`. It shipped as
    // `: false` while the default was false; leaving it there after the default
    // moved would mean leaving debug mode SILENTLY swaps the editor's bottom
    // layout, which is the stranding this fold exists to prevent, inverted.
    return this.m_debug.isEnabled ? this.collapsibleSparkRowPreference : true;
  }

  setCollapsibleSparkRowEnabled(enabled: boolean): void {
    this.write("collapsibleSparkRow", enabled);
  }

  get superGizmoPreference(): boolean {
    return this.m_settings.getDevSetting("superGizmo");
  }

  get isSuperGizmoEnabled(): boolean {
    // OPT-IN, and now the only one: the stock Select gizmo is the standard
    // TransformControls, so an ungated build — and an account where nobody
    // has touched the switch — gets `false`.
    return this.m_debug.isEnabled ? this.superGizmoPreference : false;
  }

  setSuperGizmoEnabled(enabled: boolean): void {
    this.write("superGizmo", enabled);
  }

  get webRtcRealtimeTransportPreference(): boolean {
    return this.m_settings.getDevSetting("webRtcRealtimeTransport");
  }

  get isWebRtcRealtimeTransportEnabled(): boolean {
    // OPT-OUT like responsesVoice: WebRTC is the shipped transport (user
    // direction, 2026-08-28), so an ungated build — and an account where nobody
    // has touched the switch — gets `true`, and the switch only ever turns it
    // OFF for a developer who wants the WebSocket back.
    //
    // ⚠ The ungated branch must equal `K_DEV_SETTING_DEFAULTS`. It shipped as
    // `: false` while the default was false; leaving it there after the default
    // moved would mean leaving debug mode SILENTLY downgrades the transport,
    // which is the stranding this fold exists to prevent, inverted. It
    // typechecks clean and looks correct for as long as the panel is open.
    return this.m_debug.isEnabled ? this.webRtcRealtimeTransportPreference : true;
  }

  setWebRtcRealtimeTransportEnabled(enabled: boolean): void {
    this.write("webRtcRealtimeTransport", enabled);
  }

  /**
   * The setter guard from `Campfire_Web_UI.md`: outside debug mode there is no
   * switch on screen, so nothing may write a preference that could not then be
   * undone. Doubly so now that the write is account-wide.
   *
   * The emit is the settings model's job — it fires `onChanged` synchronously on
   * the optimistic apply — so there is nothing to emit here, and the awaited
   * result is deliberately dropped: a failed publish is surfaced through
   * `IAppSettingsModel.lastError`, which the panel renders, rather than thrown
   * at a `void` setter its callers cannot handle.
   */
  private write(key: AppDevSettingKey, enabled: boolean): void {
    if (!this.m_debug.isEnabled) return;
    void this.m_settings.setDevSetting(key, enabled);
  }
}

/**
 * Drops the pre-`app-settings.json` keys from any browser that still carries
 * them. Nothing reads them, so they are inert — but `AppCacheModel`'s "Clear
 * Cache" deliberately leaves the `Campfire.Dev.*` space alone, so without this
 * they would sit in every developer's profile indefinitely, reading like live
 * settings to the next person who goes looking for where these are stored.
 *
 * Deletion, not migration: they were per-browser by design, and treating one as
 * the account's truth would have each developer's first visit publishing their
 * own copy over everyone else's.
 *
 * Run ONCE PER ORIGIN, not on every boot. The origin now hosts several builds at
 * once (the default deployment plus each named channel) over one shared
 * `localStorage`, so an unconditional sweep from a newer build deletes keys an
 * older channel is still reading — see `runOncePerOrigin` for the full note.
 */
function forgetLegacyLocalPreferences(): void {
  try {
    for (const key of [
      "Campfire.Dev.Settings.WorkflowReviewAgent",
      "Campfire.Dev.Settings.UserFacingWorkflowAuthoring",
      "Campfire.Dev.Settings.CollapsibleSparkRow",
      "Campfire.Dev.Settings.Squircle",
    ]) {
      window.localStorage.removeItem(key);
    }
    // The retired Workflow Builder reviewer's per-workflow dismissal sets
    // (`Campfire.Workflow.DismissedFindings.{uid}`) — dynamic keys, so swept by
    // prefix. Nothing reads them since the reviewer's fix queue was folded into
    // the main agent's workflow-builder role.
    const K_DISMISSED_PREFIX = "Campfire.Workflow.DismissedFindings.";
    const stale: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key?.startsWith(K_DISMISSED_PREFIX)) stale.push(key);
    }
    for (const key of stale) window.localStorage.removeItem(key);
  } catch {
    // A browser refusing storage has nothing stored to clean up.
  }
}
